import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  PushDatalayersGoogleTagManager,
  TenantService,
} from '@segurosvoluntarios_vel/commons-services';
import { Subscription } from 'rxjs';
import { filter, first, take, withLatestFrom } from 'rxjs/operators';
import {
  clearError,
  loadPago,
  setInitialPagoState,
} from 'src/app/store/actions/pago.action';
import { Ruta } from 'src/app/pago/enums/Ruta.enum';
import { Pago } from 'src/app/pago/models/Pago.model';
import { ContenidoEstadoDePagoFacade } from 'src/app/pago/services/content-estado-pago-facade/content-estado-pago-facade.service';
import { ContenidoDeEstadoDePago } from 'src/app/pago/models/ContenidoDeEstadoDePago.model';
import { DatalayerEvent } from 'src/app/pago/models/DatalayerEvent.model';
import { EstadoPago } from 'src/app/pago/enums/EstadoPago.enum';
import { EventoEstadoPago } from 'src/app/pago/enums/EventoEstadoPago.enum';
import { getContenido } from 'src/app/store/actions/content.action';
import { TagPrismicPorProducto } from 'src/app/pago/enums/TagPrismicPorProducto.enum';
import { ConfiguracionDePago } from 'src/app/pago/models/ConfiguracionDePago.model';
import { SESSION_STORE } from 'src/app/pago/enums/SessionStore.enum';
import { getDatoDeConfiguracionPago } from 'src/app/shared/utils/metada.utils';
import { EstadoPagoBuilderService } from 'src/app/pago/services/estado-pago/estado-pago-builder.service';
import { DatalayerRegisterPagoService } from 'src/app/pago/services/dalayer-register/datalayer-register-pago.service';
import { PagoFacade } from 'src/app/pago/services/pago-facade/pago.facade';

@Component({
  selector: 'app-validacion',
  templateUrl: './validacion.component.html',
})
export class ValidacionComponent implements OnInit, AfterViewInit, OnDestroy {
  static readonly DASH = '-';

  @ViewChild('sectionsContainer', { read: ViewContainerRef })
  container: ViewContainerRef;
  subscription: Subscription = new Subscription();
  tenant: string;
  idPago: string;
  contenidosDeEstadosDePago: ContenidoDeEstadoDePago[];
  estadoDePagoLoaded: boolean;
  pago: Pago;
  configuracionDePago: ConfiguracionDePago;

  constructor(
    public pagoFacade: PagoFacade,
    private activatedRoute: ActivatedRoute,
    private tenantService: TenantService,
    private estadoPagoBuilderService: EstadoPagoBuilderService,
    private contenidoEstadosDePagoFacade: ContenidoEstadoDePagoFacade
  ) {
    this.getIdPagoFromUrl();
    this.getTenant();
  }

  getIdPagoFromUrl() {
    this.activatedRoute.params.pipe(take(1)).subscribe(params => {
      this.idPago = params[Ruta.ID_PAGO];
    });
  }

  ngOnInit(): void {
    this.pagoFacade.dispatch(loadPago({ idPago: this.idPago }));

    this.pagoFacade.estadoDePagoLoaded$.subscribe(
      (estadoDePagoLoaded: boolean) => {
        this.estadoDePagoLoaded = estadoDePagoLoaded;
      }
    );
  }

  getTenant() {
    this.tenantService
      .getTenant()
      .pipe(take(1))
      .subscribe((tenant: string) => (this.tenant = tenant));
  }

  ngAfterViewInit(): void {
    this.subscribeToPago();
    this.subscribeToContenidoEstadosDePago();
    this.subscribeToConfiguracionDePago();
  }

  subscribeToPago(): void {
    this.subscription.add(
      this.pagoFacade.pago$.subscribe((pago: Pago) => {
        this.pagoFacade.dispatch(
          getContenido({
            tenant: this.tenant.toLocaleLowerCase(),
            producto: TagPrismicPorProducto[pago.idProducto],
          })
        );
      })
    );
  }

  subscribeToConfiguracionDePago(): void {
    this.subscription.add(
      this.pagoFacade.configuracionDePago$.subscribe(
        (configuracionPago: ConfiguracionDePago) => {
          this.configuracionDePago = configuracionPago;
        }
      )
    );
  }

  subscribeToContenidoEstadosDePago(): void {
    this.contenidoEstadosDePagoFacade.contenidoEstadosDePagos$
      .pipe(
        withLatestFrom(this.pagoFacade.pago$),
        filter(([contenidosDeEstadosDePago, pago]) => !!pago.estadoPago),
        first()
      )
      .subscribe(([contenidosDeEstadosDePago, pago]) => {
        this.crearComponente(contenidosDeEstadosDePago, pago);
      });
  }

  crearComponente(
    contenidosDeEstadosDePago: ContenidoDeEstadoDePago[],
    pago: Pago
  ) {
    const { estadoPago } = pago;
    switch (estadoPago) {
      case EstadoPago.APROBADO:
        this.handlePushDatalayer(pago, EventoEstadoPago.APROBADO);
        break;
      case EstadoPago.RECHAZADO:
        this.handlePushDatalayer(pago, EventoEstadoPago.RECHAZADO);
        break;
      case EstadoPago.PENDIENTE:
        this.handlePushDatalayer(pago, EventoEstadoPago.PENDIENTE);
        break;
      case EstadoPago.EXPIRADO:
        this.handlePushDatalayer(pago, EventoEstadoPago.EXPIRADO);
        break;
    }
    this.estadoPagoBuilderService.createComponent(
      pago,
      contenidosDeEstadosDePago,
      this.container,
      this.tenant
    );
  }

  handlePushDatalayer(pago: Pago, event: string) {
    let typeOfPushGtm = event;

    if (event.includes(ValidacionComponent.DASH)) {
      const splitEvent = event.split(ValidacionComponent.DASH);
      typeOfPushGtm =
        splitEvent[0] +
        splitEvent[1].charAt(0).toUpperCase() +
        splitEvent[1].slice(1).toLocaleLowerCase();
    }

    const storedPrepoliza = sessionStorage.getItem(SESSION_STORE.PREPOLIZA);
    const metadataPrepoliza = getDatoDeConfiguracionPago(
      this.configuracionDePago,
      DatalayerRegisterPagoService.PREPOLIZA
    );
    const prepoliza = metadataPrepoliza
      ? metadataPrepoliza
      : pago.referenciaExterna;

    PushDatalayersGoogleTagManager.pushObject<DatalayerEvent>({
      ecommerce: {
        [typeOfPushGtm]: {
          actionField: {
            transactionId: pago.cdAviso || pago.transaccion,
            transactionPoliza: storedPrepoliza || prepoliza,
            transactionAffiliation: this.tenant,
            transactionTotal: +pago.total,
            transactionProducts: {
              sku: storedPrepoliza || prepoliza,
              name: pago.idProducto,
              price: +pago.total,
              quantity: 1,
            },
          },
        },
      },
      event,
    });
  }

  clearErrorModal() {
    this.pagoFacade.dispatch(clearError());
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.pagoFacade.dispatch(setInitialPagoState());
  }
}
